import React, { useEffect, useRef, useState, useCallback } from 'react';
import Topbar from '../components/Topbar';
import { ChevronDown, MessageSquare, Send, User, Shield, Paperclip, X, ImageIcon } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../api';
import { useApp } from '../context/AppContext';
import { compressImage, approxSizeKB } from '../utils/image';

const FAQS = [
  { q: 'How does delivery work?', a: 'Digital orders are auto-delivered to your account within seconds after payment confirmation.' },
  { q: 'Which payment methods are supported?', a: 'USDT (TRC-20 / BEP-20) and INR via UPI. Deposits are credited after admin verification.' },
  { q: 'What is your refund policy?', a: 'Faulty items are replaced instantly. Refunds processed within 24h if replacement is unavailable.' },
  { q: 'Do you offer warranty on subscriptions?', a: 'Yes — selected items include "FULL WARRANTY" tag with lifetime replacement during the plan period.' },
];

const MessageBubble = ({ m, onImageClick }) => {
  const mine = m.from === 'user';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] flex gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'} items-end`}>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center border shrink-0 ${mine ? 'border-[#1f1f1f] text-[#8a8a8a]' : 'border-[#22c55e]/40 text-[#22c55e] bg-[#0f1a12]'}`}>
          {mine ? <User size={12} /> : <Shield size={12} />}
        </div>
        <div className="min-w-0">
          <div className={`rounded-md overflow-hidden ${
            mine ? 'bg-[#0d1a11] border border-[#1f3a24]' : 'bg-[#0d0d0d] border border-[#1f1f1f]'
          }`}>
            {m.image && (
              <button
                type="button"
                onClick={() => onImageClick(m.image)}
                className="block w-full max-w-[280px]"
              >
                <img src={m.image} alt="attachment" className="w-full h-auto max-h-[260px] object-cover" />
              </button>
            )}
            {m.text && (
              <div className="px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap break-words text-[#e6e6e6]">
                {m.text}
              </div>
            )}
          </div>
          <div className={`text-[10px] text-[#5a5a5a] mt-0.5 ${mine ? 'text-right' : 'text-left'}`}>
            {m.created_at}
          </div>
        </div>
      </div>
    </div>
  );
};

const Support = ({ onDeposit }) => {
  const { storeSettings } = useApp();
  const [openIdx, setOpenIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [image, setImage] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/support/messages');
      setMessages(r.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast({ title: 'Only images allowed' });
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Max 15MB — will be compressed.' });
    }
    setPreparing(true);
    try {
      const dataUrl = await compressImage(f, { maxDim: 1600, quality: 0.75 });
      setImage(dataUrl);
    } catch {
      toast({ title: 'Failed to load image' });
    } finally {
      setPreparing(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const send = async () => {
    const val = text.trim();
    if ((!val && !image) || sending) return;
    setSending(true);
    const tmp = { id: 'tmp-' + Date.now(), from: 'user', text: val, image, created_at: 'sending…', _tmp: true };
    setMessages((m) => [...m, tmp]);
    setText('');
    const imgToSend = image;
    setImage(null);
    try {
      await api.post('/support/messages', { text: val, image: imgToSend });
      await load();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tmp.id));
      toast({ title: 'Failed to send', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div>
      <Topbar count={messages.length} title="SUPPORT" subtitleUnit="Messages" onDeposit={onDeposit} />
      <div className="px-8 py-6 font-mono grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="border border-[#151515] rounded-md bg-[#0a0a0a] flex flex-col h-[calc(100vh-180px)] min-h-[500px]">
          <div className="px-4 py-3 border-b border-[#151515] flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#22c55e]">
              <MessageSquare size={14} />
              <span className="text-[10px] tracking-[0.25em]">LIVE CHAT WITH SUPPORT</span>
            </div>
            <span className="text-[10px] tracking-[0.2em] text-[#5a5a5a]">
              {storeSettings?.support_telegram ? storeSettings.support_telegram : 'ADMIN ONLINE'}
            </span>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-[12px] text-[#5a5a5a] py-16">
                No messages yet. Send us your question below —<br />we reply within 15 minutes.
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} onImageClick={setLightbox} />
            ))}
          </div>

          {/* Attachment preview */}
          {image && (
            <div className="px-3 pt-3">
              <div className="relative inline-block border border-[#22c55e]/40 rounded-md overflow-hidden">
                <img src={image} alt="preview" className="max-h-[100px] w-auto" />
                <button
                  onClick={() => setImage(null)}
                  className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-[#e6e6e6] hover:text-[#ef4444]"
                >
                  <X size={12} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[9px] text-[#22c55e] tracking-[0.2em] px-2 py-0.5">
                  {approxSizeKB(image)} KB · READY
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-[#151515] p-3">
            <div className="flex items-end gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickFile}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={preparing || sending}
                title="Attach screenshot"
                className="p-2.5 rounded-md border border-[#1f1f1f] text-[#8a8a8a] hover:border-[#22c55e] hover:text-[#22c55e] disabled:opacity-50 transition-colors"
              >
                {preparing ? <ImageIcon size={14} className="animate-pulse text-[#22c55e]" /> : <Paperclip size={14} />}
              </button>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                placeholder="Type your message… (Enter to send, Shift+Enter for newline)"
                className="flex-1 bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm text-[#e6e6e6] outline-none focus:border-[#22c55e] transition-colors resize-none"
              />
              <button
                onClick={send}
                disabled={sending || (!text.trim() && !image)}
                className="px-4 py-2.5 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] disabled:opacity-50 text-[#e7ffef] transition-colors flex items-center gap-2"
              >
                <Send size={12} /> SEND
              </button>
            </div>
            <p className="text-[10px] text-[#5a5a5a] mt-2">Tip: Attach screenshots for faster resolution. Images auto-compressed.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-[#151515] rounded-md bg-[#0a0a0a] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#151515] text-[10px] tracking-[0.25em] text-[#5a5a5a]">FAQ</div>
            {FAQS.map((f, i) => (
              <div key={i} className="border-b border-[#0f0f0f] last:border-0">
                <button
                  onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                  className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-[#0d0d0d] transition-colors"
                >
                  <span className="text-[12px] text-[#e6e6e6]">{f.q}</span>
                  <ChevronDown size={12} className={`text-[#5a5a5a] transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />
                </button>
                {openIdx === i && (
                  <div className="px-4 pb-3 text-[11px] text-[#8a8a8a] leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
          <div className="border border-[#151515] rounded-md bg-[#0a0a0a] p-4">
            <div className="text-[10px] tracking-[0.25em] text-[#5a5a5a] mb-2">CONTACT</div>
            <div className="space-y-1.5 text-[11px] text-[#8a8a8a]">
              {storeSettings?.support_telegram && (
                <div><span className="text-[#5a5a5a] tracking-[0.2em] mr-1">TG ·</span> {storeSettings.support_telegram}</div>
              )}
              <div><span className="text-[#5a5a5a] tracking-[0.2em] mr-1">SLA ·</span> 24/7 · &lt; 15 min avg reply</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6" onClick={() => setLightbox(null)}>
          <div className="absolute inset-0 bg-black/90" />
          <button className="absolute top-4 right-4 z-10 bg-black/70 rounded-full p-2 text-[#e6e6e6] hover:text-[#ef4444]">
            <X size={18} />
          </button>
          <img src={lightbox} alt="full" className="relative max-w-full max-h-full rounded-md" />
        </div>
      )}
    </div>
  );
};

export default Support;
